import React, { forwardRef, useLayoutEffect, useRef } from "react";
import classNames from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import Portal from "../../layout/Portal";
import TagWrapper from "../TagWrapper";
import useEventListener from "../../../Hooks/useEventListener";

export const ModalTitle = forwardRef(
  ({ tag = "h5", id, children, className, ...props }, ref) => {
    return (
      <TagWrapper
        tag={tag}
        ref={ref}
        id={id}
        className={classNames("modal-title", className)}
        {...props}
      >
        {children}
      </TagWrapper>
    );
  }
);
ModalTitle.displayName = "ModalTitle";

export const ModalHeader = forwardRef(
  ({ children, className, setIsOpen, isCrossBtnShow, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={classNames("modal-header", className)}
        {...props}
      >
        {children}
        {setIsOpen && isCrossBtnShow && (
          <button
            type="button"
            className="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
            onClick={() => setIsOpen(false)}
          />
        )}
      </div>
    );
  }
);
ModalHeader.displayName = "ModalHeader";

export const ModalBody = forwardRef(
  ({ children, className, ...props }, ref) => {
    return (
      <div ref={ref} className={classNames("modal-body", className)} {...props}>
        {children}
      </div>
    );
  }
);
ModalBody.displayName = "ModalBody";

export const ModalFooter = forwardRef(
  ({ children, className, isBtnShow, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={classNames("modal-footer", className)}
        {...props}
      >
        {isBtnShow ? children : null}
      </div>
    );
  }
);
ModalFooter.displayName = "ModalFooter";

const BaseModal = ({
  children,
  isOpen,
  setIsOpen,
  id,
  titleId,
  isStaticBackdrop,
  isScrollable,
  isCentered,
  size,
  fullScreen,
  isAnimation = true,
  ...props
}) => {
  const refModal = useRef(null);
  const ref = useRef(null);

  // <body> modal-open class
  useLayoutEffect(() => {
    if (isOpen) {
      document.body.classList.add("modal-open");
    }
    return () => {
      document.body.classList.remove("modal-open");
    };
  });

  // Backdrop close function
  const closeModal = (event) => {
    if (
      ref.current &&
      !ref.current.contains(event.target) &&
      !isStaticBackdrop
    ) {
      setIsOpen(false);
    }
  };
  useEventListener("mousedown", closeModal);
  useEventListener("touchstart", closeModal);

  // Backdrop static function
  const modalStatic = (event) => {
    if (
      ref.current &&
      !ref.current.contains(event.target) &&
      isStaticBackdrop
    ) {
      if (refModal.current) {
        refModal.current.classList.add("modal-static");
        setTimeout(
          () => refModal.current.classList.remove("modal-static"),
          300
        );
      }
    }
  };
  useEventListener("mousedown", modalStatic);
  useEventListener("touchstart", modalStatic);

  // Keypress close function
  const escFunction = (event) => {
    if (event.key === "Escape") {
      setIsOpen(false);
    }
  };
  useEventListener("keydown", escFunction);

  const ANIMATION_PROPS = isAnimation
    ? {
        initial: { opacity: 0, y: "-50%" },
        animate: { opacity: 1, x: "0%", y: "0%" },
        exit: { opacity: 0, y: "-50%" },
        transition: { ease: "easeInOut", duration: 0.3 },
      }
    : null;

  return (
    <Portal>
      <AnimatePresence mode="wait">
        {isOpen && (
          <>
            <motion.div
              ref={refModal}
              key="modal"
              className={classNames("modal", { fade: isAnimation }, "show")}
              role="dialog"
              style={{ display: "block" }}
              id={id}
              tabIndex={-1}
              aria-labelledby={titleId}
              aria-hidden="true"
              data-bs-backdrop={isStaticBackdrop ? "static" : null}
              data-bs-keyboard={isStaticBackdrop ? "false" : null}
              {...ANIMATION_PROPS}
              {...props}
            >
              <div
                ref={ref}
                className={classNames("modal-dialog", {
                  "modal-dialog-scrollable": isScrollable,
                  "modal-dialog-centered": isCentered,
                  [`modal-${size}`]: size,
                  [`modal-fullscreen${
                    typeof fullScreen === "string" ? `-${fullScreen}-down` : ""
                  }`]: fullScreen,
                })}
              >
                <div className="modal-content">{children}</div>
              </div>
            </motion.div>
            <div
              className={classNames(
                "modal-backdrop",
                { fade: isAnimation },
                "show"
              )}
            />
          </>
        )}
      </AnimatePresence>
    </Portal>
  );
};

export default BaseModal;